import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/Navbar';
import { getModerationApiBaseUrl } from '../config/api';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

// ------------------------------------------------------------------ //
//  Types                                                               //
// ------------------------------------------------------------------ //

interface Overview {
  period_days: number;
  totals: {
    totalEvents: number;
    totalBlocked: number;
    totalActive: number;
    uniqueUsers: number;
    blockRate: number;
  };
  byCategory: { category: string; total: number; blocked: number; active: number }[];
  daily: { date: string; total: number; blocked: number; active: number }[];
  byReason: { reason: string; count: number }[];
}

interface Violator {
  UserID: string;
  UserName: string;
  Country: string;
  TotalEvents: number;
  TotalViolations: number;
  LastViolationAt: string;
  Categories: Record<string, { TotalEvents?: number; TotalViolations?: number }>;
}

interface QuarantineKey {
  original: string;
  quarantine: string;
}

interface ModerationEvent {
  _id: string;
  UserID: string;
  UserName: string;
  Category: string;
  ContentURL: string;
  Status: string;
  Reason: string;
  Scores: Record<string, number>;
  QuarantineKeys?: QuarantineKey[];
  RecoveredAt?: string;
  createdAt: string;
}

interface Realtime {
  timestamp: string;
  lastHour: { total: number; blocked: number };
  today: { total: number; blocked: number };
  queue: { pending: number };
  service: { r2: string; liveEnforcement: boolean };
}

// ------------------------------------------------------------------ //
//  Helpers                                                             //
// ------------------------------------------------------------------ //

const CATEGORY_LABELS: Record<string, string> = {
  profile_image: 'Profile Images',
  comment_image: 'Comment Images',
  video: 'Videos',
  text_comment: 'Text Comments',
  commerce_product: 'Commerce',
};

const CHART_COLORS = ['#FF9C15', '#F42026', '#3B82F6', '#10B981', '#8B5CF6'];

const PIE_COLORS = ['#F42026', '#10B981'];

const formatNumber = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
};

const timeAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

// ------------------------------------------------------------------ //
//  Component                                                           //
// ------------------------------------------------------------------ //

const ImageModeration: React.FC = () => {
  const [tab, setTab] = useState<'overview' | 'events' | 'violators'>('overview');
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [violators, setViolators] = useState<Violator[]>([]);
  const [events, setEvents] = useState<ModerationEvent[]>([]);
  const [realtime, setRealtime] = useState<Realtime | null>(null);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState({ category: '', status: '' });
  const [recoveringId, setRecoveringId] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<{ url: string; eventId: string } | null>(null);

  const api = getModerationApiBaseUrl();

  const fetchAll = useCallback(async () => {
    try {
      const [overviewRes, violatorsRes, eventsRes, realtimeRes] = await Promise.all([
        fetch(`${api}/api/analytics/overview?days=${days}`),
        fetch(`${api}/api/analytics/top-violators?limit=20`),
        fetch(`${api}/api/analytics/recent-events?limit=50&category=${eventFilter.category}&status=${eventFilter.status}`),
        fetch(`${api}/api/analytics/realtime`),
      ]);
      const [ov, vl, ev, rt] = await Promise.all([
        overviewRes.json(), violatorsRes.json(), eventsRes.json(), realtimeRes.json(),
      ]);
      setOverview(ov);
      setViolators(vl.violators || []);
      setEvents(ev.events || []);
      setRealtime(rt);
    } catch (err) {
      console.error('Failed to fetch moderation analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [api, days, eventFilter]);

  const handleRecover = useCallback(async (eventId: string) => {
    if (!window.confirm('Are you sure you want to recover this image? It will be restored to its original location.')) return;
    setRecoveringId(eventId);
    try {
      const res = await fetch(`${api}/api/moderation/recover/${eventId}`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        await fetchAll();
      } else {
        alert(`Recovery failed: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Recovery failed: ${err}`);
    } finally {
      setRecoveringId(null);
    }
  }, [api, fetchAll]);

  const getQuarantinePreviewUrl = useCallback((ev: ModerationEvent) => {
    if (!ev.QuarantineKeys?.length) return null;
    const key = ev.QuarantineKeys[0].original;
    return `${api}/api/moderation/quarantine-preview?key=${encodeURIComponent(key)}`;
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const totals = overview?.totals || { totalEvents: 0, totalBlocked: 0, totalActive: 0, uniqueUsers: 0, blockRate: 0 };

  // Pie chart data for block/pass ratio
  const pieData = [
    { name: 'Blocked', value: totals.totalBlocked },
    { name: 'Passed', value: totals.totalActive },
  ];

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Header */}
        <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8'>
          <div>
            <h1 className='text-3xl font-bold text-white'>
              <span className='bg-gradient-to-r from-[#FF9C15] to-[#F42026] bg-clip-text text-transparent'>
                Image Moderation
              </span>{' '}
              Analytics
            </h1>
            <p className='text-gray-400 mt-1'>AI-powered content moderation monitoring & reputation tracking</p>
          </div>
          <div className='flex items-center gap-3'>
            {/* Live indicator */}
            <div className='flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full border border-white/10'>
              <div className='w-2 h-2 bg-green-400 rounded-full animate-pulse' />
              <span className='text-xs text-gray-300'>Live</span>
            </div>
            {/* Period selector */}
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className='bg-white/5 border border-white/10 text-gray-300 text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#FF9C15]/50'
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={() => { setLoading(true); fetchAll(); }}
              className='px-4 py-1.5 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white text-sm rounded-lg hover:opacity-90 transition-opacity'
            >
              Refresh
            </button>
          </div>
        </div>

        {loading && !overview ? (
          <div className='flex items-center justify-center h-64'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF9C15]' />
          </div>
        ) : (
          <>
            {/* Real-time status bar */}
            {realtime && (
              <div className='grid grid-cols-2 md:grid-cols-5 gap-3 mb-6'>
                {[
                  { label: 'Last Hour', value: realtime.lastHour.total, sub: `${realtime.lastHour.blocked} blocked`, color: 'text-blue-400' },
                  { label: 'Today', value: realtime.today.total, sub: `${realtime.today.blocked} blocked`, color: 'text-purple-400' },
                  { label: 'Queue', value: realtime.queue.pending, sub: 'pending', color: realtime.queue.pending > 50 ? 'text-red-400' : 'text-green-400' },
                  { label: 'R2 Storage', value: realtime.service.r2 === 'connected' ? 'OK' : 'Off', sub: 'deletion', color: realtime.service.r2 === 'connected' ? 'text-green-400' : 'text-red-400' },
                  { label: 'Enforcement', value: realtime.service.liveEnforcement ? 'Live' : 'Soft', sub: 'mode', color: realtime.service.liveEnforcement ? 'text-green-400' : 'text-yellow-400' },
                ].map((s) => (
                  <div key={s.label} className='bg-white/5 border border-white/10 rounded-xl p-3 text-center'>
                    <div className={`text-lg font-bold ${s.color}`}>{typeof s.value === 'number' ? formatNumber(s.value) : s.value}</div>
                    <div className='text-xs text-gray-500'>{s.label}</div>
                    <div className='text-[10px] text-gray-600'>{s.sub}</div>
                  </div>
                ))}
              </div>
            )}

            {/* KPI Cards */}
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-8'>
              {[
                { label: 'Total Scanned', value: totals.totalEvents, icon: '🔍', gradient: 'from-blue-500/20 to-blue-600/20', border: 'border-blue-500/30' },
                { label: 'Blocked', value: totals.totalBlocked, icon: '🚫', gradient: 'from-red-500/20 to-red-600/20', border: 'border-red-500/30' },
                { label: 'Passed', value: totals.totalActive, icon: '✅', gradient: 'from-green-500/20 to-green-600/20', border: 'border-green-500/30' },
                { label: 'Block Rate', value: `${totals.blockRate}%`, icon: '📊', gradient: 'from-purple-500/20 to-purple-600/20', border: 'border-purple-500/30' },
              ].map((kpi) => (
                <div key={kpi.label} className={`bg-gradient-to-br ${kpi.gradient} border ${kpi.border} rounded-2xl p-5`}>
                  <div className='text-2xl mb-2'>{kpi.icon}</div>
                  <div className='text-2xl sm:text-3xl font-bold text-white'>
                    {typeof kpi.value === 'number' ? formatNumber(kpi.value) : kpi.value}
                  </div>
                  <div className='text-sm text-gray-400 mt-1'>{kpi.label}</div>
                </div>
              ))}
            </div>

            {/* Tab Navigation */}
            <div className='flex gap-1 mb-6 bg-white/5 rounded-xl p-1 border border-white/10 w-fit'>
              {(['overview', 'events', 'violators'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                    tab === t
                      ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {t === 'overview' && 'Overview'}
                  {t === 'events' && 'Recent Events'}
                  {t === 'violators' && 'Top Violators'}
                </button>
              ))}
            </div>

            {/* === OVERVIEW TAB === */}
            {tab === 'overview' && overview && (
              <div className='space-y-6'>
                {/* Daily timeline chart */}
                <div className='bg-white/5 border border-white/10 rounded-2xl p-6'>
                  <h3 className='text-lg font-semibold text-white mb-4'>Moderation Activity</h3>
                  <ResponsiveContainer width='100%' height={300}>
                    <AreaChart data={overview.daily}>
                      <defs>
                        <linearGradient id='gradActive' x1='0' y1='0' x2='0' y2='1'>
                          <stop offset='5%' stopColor='#10B981' stopOpacity={0.3} />
                          <stop offset='95%' stopColor='#10B981' stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id='gradBlocked' x1='0' y1='0' x2='0' y2='1'>
                          <stop offset='5%' stopColor='#F42026' stopOpacity={0.3} />
                          <stop offset='95%' stopColor='#F42026' stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray='3 3' stroke='rgba(255,255,255,0.05)' />
                      <XAxis
                        dataKey='date'
                        stroke='#6B7280'
                        fontSize={11}
                        tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis stroke='#6B7280' fontSize={11} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1E293B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }}
                        labelFormatter={(d) => new Date(d).toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' })}
                      />
                      <Legend />
                      <Area type='monotone' dataKey='active' name='Passed' stroke='#10B981' fill='url(#gradActive)' strokeWidth={2} />
                      <Area type='monotone' dataKey='blocked' name='Blocked' stroke='#F42026' fill='url(#gradBlocked)' strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                  {/* Category breakdown */}
                  <div className='bg-white/5 border border-white/10 rounded-2xl p-6'>
                    <h3 className='text-lg font-semibold text-white mb-4'>By Category</h3>
                    <ResponsiveContainer width='100%' height={250}>
                      <BarChart data={overview.byCategory} layout='vertical'>
                        <CartesianGrid strokeDasharray='3 3' stroke='rgba(255,255,255,0.05)' />
                        <XAxis type='number' stroke='#6B7280' fontSize={11} />
                        <YAxis
                          type='category'
                          dataKey='category'
                          stroke='#6B7280'
                          fontSize={11}
                          width={120}
                          tickFormatter={(c: string) => CATEGORY_LABELS[c] || c}
                        />
                        <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }} />
                        <Bar dataKey='active' name='Passed' stackId='a' fill='#10B981' radius={[0, 0, 0, 0]} />
                        <Bar dataKey='blocked' name='Blocked' stackId='a' fill='#F42026' radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Block rate pie + reason breakdown */}
                  <div className='bg-white/5 border border-white/10 rounded-2xl p-6'>
                    <h3 className='text-lg font-semibold text-white mb-4'>Block Reasons</h3>
                    <div className='flex items-center gap-6'>
                      <ResponsiveContainer width='45%' height={200}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx='50%'
                            cy='50%'
                            innerRadius={50}
                            outerRadius={80}
                            dataKey='value'
                            strokeWidth={0}
                          >
                            {pieData.map((_, i) => (
                              <Cell key={i} fill={PIE_COLORS[i]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className='flex-1 space-y-3'>
                        {overview.byReason.map((r, i) => (
                          <div key={r.reason} className='flex items-center justify-between'>
                            <div className='flex items-center gap-2'>
                              <div className='w-3 h-3 rounded-full' style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                              <span className='text-sm text-gray-300 capitalize'>{r.reason.replace(/_/g, ' ')}</span>
                            </div>
                            <span className='text-sm font-semibold text-white'>{r.count}</span>
                          </div>
                        ))}
                        {overview.byReason.length === 0 && (
                          <p className='text-sm text-gray-500'>No violations in this period</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Unique users stat */}
                <div className='bg-white/5 border border-white/10 rounded-2xl p-6'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <h3 className='text-lg font-semibold text-white'>Unique Users Scanned</h3>
                      <p className='text-gray-400 text-sm'>Users whose content was moderated in the last {days} days</p>
                    </div>
                    <div className='text-4xl font-bold bg-gradient-to-r from-[#FF9C15] to-[#F42026] bg-clip-text text-transparent'>
                      {formatNumber(totals.uniqueUsers)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* === RECENT EVENTS TAB === */}
            {tab === 'events' && (
              <div className='space-y-4'>
                {/* Filters */}
                <div className='flex gap-3 mb-4'>
                  <select
                    value={eventFilter.category}
                    onChange={(e) => setEventFilter((f) => ({ ...f, category: e.target.value }))}
                    className='bg-white/5 border border-white/10 text-gray-300 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#FF9C15]/50'
                  >
                    <option value=''>All Categories</option>
                    <option value='profile_image'>Profile Images</option>
                    <option value='comment_image'>Comment Images</option>
                  </select>
                  <select
                    value={eventFilter.status}
                    onChange={(e) => setEventFilter((f) => ({ ...f, status: e.target.value }))}
                    className='bg-white/5 border border-white/10 text-gray-300 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#FF9C15]/50'
                  >
                    <option value=''>All Statuses</option>
                    <option value='active'>Passed</option>
                    <option value='blocked'>Blocked</option>
                    <option value='recovered'>Recovered</option>
                  </select>
                </div>

                {/* Events table */}
                <div className='bg-white/5 border border-white/10 rounded-2xl overflow-hidden'>
                  <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                      <thead>
                        <tr className='border-b border-white/10'>
                          <th className='text-left py-3 px-4 text-gray-400 font-medium'>Image</th>
                          <th className='text-left py-3 px-4 text-gray-400 font-medium'>User</th>
                          <th className='text-left py-3 px-4 text-gray-400 font-medium'>Category</th>
                          <th className='text-left py-3 px-4 text-gray-400 font-medium'>Status</th>
                          <th className='text-left py-3 px-4 text-gray-400 font-medium'>Reason</th>
                          <th className='text-left py-3 px-4 text-gray-400 font-medium'>Scores</th>
                          <th className='text-left py-3 px-4 text-gray-400 font-medium'>When</th>
                          <th className='text-left py-3 px-4 text-gray-400 font-medium'>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((ev) => {
                          const previewUrl = ev.Status === 'blocked' && ev.QuarantineKeys?.length
                            ? getQuarantinePreviewUrl(ev)
                            : ev.ContentURL
                              ? `https://cdn2.pingtop.co/${ev.ContentURL.replace(/^\//, '')}`
                              : null;

                          return (
                            <tr key={ev._id} className='border-b border-white/5 hover:bg-white/5 transition-colors'>
                              <td className='py-3 px-4'>
                                {previewUrl ? (
                                  <button
                                    onClick={() => setPreviewImage({ url: previewUrl, eventId: ev._id })}
                                    className='relative w-12 h-12 rounded-lg overflow-hidden border border-white/10 hover:border-[#FF9C15]/50 transition-colors group'
                                  >
                                    <img
                                      src={previewUrl}
                                      alt='moderated'
                                      className='w-full h-full object-cover'
                                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                    {ev.Status === 'blocked' && (
                                      <div className='absolute inset-0 bg-red-500/20 flex items-center justify-center'>
                                        <svg className='w-5 h-5 text-red-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636' />
                                        </svg>
                                      </div>
                                    )}
                                    <div className='absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center'>
                                      <svg className='w-4 h-4 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7' />
                                      </svg>
                                    </div>
                                  </button>
                                ) : (
                                  <div className='w-12 h-12 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center'>
                                    <svg className='w-5 h-5 text-gray-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' />
                                    </svg>
                                  </div>
                                )}
                              </td>
                              <td className='py-3 px-4'>
                                <div className='text-white font-medium'>{ev.UserName}</div>
                                <div className='text-xs text-gray-500 font-mono'>{ev.UserID?.slice(0, 8)}...</div>
                              </td>
                              <td className='py-3 px-4'>
                                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20'>
                                  {CATEGORY_LABELS[ev.Category] || ev.Category}
                                </span>
                              </td>
                              <td className='py-3 px-4'>
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                  ev.Status === 'blocked'
                                    ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                                    : ev.Status === 'recovered'
                                      ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                                      : 'bg-green-500/10 text-green-400 border border-green-500/20'
                                }`}>
                                  <div className={`w-1.5 h-1.5 rounded-full ${
                                    ev.Status === 'blocked' ? 'bg-red-400' : ev.Status === 'recovered' ? 'bg-yellow-400' : 'bg-green-400'
                                  }`} />
                                  {ev.Status === 'blocked' ? 'Blocked' : ev.Status === 'recovered' ? 'Recovered' : 'Passed'}
                                </span>
                              </td>
                              <td className='py-3 px-4 text-gray-300 capitalize'>
                                {ev.Reason?.replace(/_/g, ' ')}
                              </td>
                              <td className='py-3 px-4'>
                                {ev.Scores && (
                                  <div className='space-y-1'>
                                    {Object.entries(ev.Scores).map(([k, v]) => (
                                      <div key={k} className='flex items-center gap-2'>
                                        <div className='w-16 h-1.5 bg-white/10 rounded-full overflow-hidden'>
                                          <div
                                            className={`h-full rounded-full ${(v as number) > 0.8 ? 'bg-red-500' : (v as number) > 0.5 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                            style={{ width: `${(v as number) * 100}%` }}
                                          />
                                        </div>
                                        <span className='text-[10px] text-gray-500'>{((v as number) * 100).toFixed(0)}%</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                              <td className='py-3 px-4 text-gray-500 text-xs whitespace-nowrap'>
                                {ev.createdAt ? timeAgo(ev.createdAt) : '-'}
                              </td>
                              <td className='py-3 px-4'>
                                {ev.Status === 'blocked' && ev.QuarantineKeys?.length ? (
                                  <button
                                    onClick={() => handleRecover(ev._id)}
                                    disabled={recoveringId === ev._id}
                                    className='inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 disabled:from-gray-500 disabled:to-gray-600 text-white text-xs font-medium rounded-lg transition-all shadow-sm hover:shadow-md'
                                  >
                                    {recoveringId === ev._id ? (
                                      <div className='w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                                    ) : (
                                      <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' />
                                      </svg>
                                    )}
                                    {recoveringId === ev._id ? 'Recovering...' : 'Recover'}
                                  </button>
                                ) : ev.Status === 'recovered' ? (
                                  <span className='text-xs text-yellow-400/70'>
                                    {ev.RecoveredAt ? timeAgo(ev.RecoveredAt) : 'Restored'}
                                  </span>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })}
                        {events.length === 0 && (
                          <tr>
                            <td colSpan={8} className='text-center py-12 text-gray-500'>
                              No moderation events found. Events will appear here once images are processed.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* === TOP VIOLATORS TAB === */}
            {tab === 'violators' && (
              <div className='bg-white/5 border border-white/10 rounded-2xl overflow-hidden'>
                <div className='overflow-x-auto'>
                  <table className='w-full text-sm'>
                    <thead>
                      <tr className='border-b border-white/10'>
                        <th className='text-left py-3 px-4 text-gray-400 font-medium'>#</th>
                        <th className='text-left py-3 px-4 text-gray-400 font-medium'>User</th>
                        <th className='text-left py-3 px-4 text-gray-400 font-medium'>Total Events</th>
                        <th className='text-left py-3 px-4 text-gray-400 font-medium'>Violations</th>
                        <th className='text-left py-3 px-4 text-gray-400 font-medium'>Profile</th>
                        <th className='text-left py-3 px-4 text-gray-400 font-medium'>Comments</th>
                        <th className='text-left py-3 px-4 text-gray-400 font-medium'>Last Violation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {violators.map((v, idx) => (
                        <tr key={v.UserID} className='border-b border-white/5 hover:bg-white/5 transition-colors'>
                          <td className='py-3 px-4'>
                            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                              idx < 3 ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white' : 'bg-white/10 text-gray-400'
                            }`}>
                              {idx + 1}
                            </span>
                          </td>
                          <td className='py-3 px-4'>
                            <div className='text-white font-medium'>{v.UserName}</div>
                            <div className='text-xs text-gray-500'>
                              <span className='font-mono'>{v.UserID?.slice(0, 8)}...</span>
                              {v.Country && <span className='ml-2'>{v.Country}</span>}
                            </div>
                          </td>
                          <td className='py-3 px-4 text-gray-300'>{v.TotalEvents}</td>
                          <td className='py-3 px-4'>
                            <span className='inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-500/10 text-red-400 border border-red-500/20'>
                              {v.TotalViolations}
                            </span>
                          </td>
                          <td className='py-3 px-4 text-gray-400'>
                            {v.Categories?.profile_image?.TotalViolations || 0}
                            <span className='text-gray-600'>/{v.Categories?.profile_image?.TotalEvents || 0}</span>
                          </td>
                          <td className='py-3 px-4 text-gray-400'>
                            {v.Categories?.comment_image?.TotalViolations || 0}
                            <span className='text-gray-600'>/{v.Categories?.comment_image?.TotalEvents || 0}</span>
                          </td>
                          <td className='py-3 px-4 text-gray-500 text-xs whitespace-nowrap'>
                            {v.LastViolationAt ? timeAgo(v.LastViolationAt) : '-'}
                          </td>
                        </tr>
                      ))}
                      {violators.length === 0 && (
                        <tr>
                          <td colSpan={7} className='text-center py-12 text-gray-500'>
                            No violators found yet. Users with violations will appear here.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm'
          onClick={() => setPreviewImage(null)}
        >
          <div
            className='relative max-w-2xl max-h-[80vh] bg-slate-900 border border-white/10 rounded-2xl overflow-hidden shadow-2xl'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='flex items-center justify-between p-4 border-b border-white/10'>
              <h3 className='text-sm font-medium text-gray-300'>Quarantined Image Preview</h3>
              <div className='flex items-center gap-2'>
                {events.find(e => e._id === previewImage.eventId)?.Status === 'blocked' && (
                  <button
                    onClick={() => {
                      handleRecover(previewImage.eventId);
                      setPreviewImage(null);
                    }}
                    className='inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-xs font-medium rounded-lg transition-all'
                  >
                    <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' />
                    </svg>
                    Recover Image
                  </button>
                )}
                <button
                  onClick={() => setPreviewImage(null)}
                  className='p-1.5 hover:bg-white/10 rounded-lg transition-colors'
                >
                  <svg className='w-5 h-5 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                  </svg>
                </button>
              </div>
            </div>
            <div className='p-4'>
              <img
                src={previewImage.url}
                alt='Quarantined content'
                className='max-w-full max-h-[60vh] object-contain rounded-lg mx-auto'
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).parentElement!.innerHTML = '<div class="flex items-center justify-center h-48 text-gray-500 text-sm">Image expired or unavailable (72h TTL)</div>';
                }}
              />
            </div>
            <div className='px-4 pb-4'>
              <p className='text-xs text-gray-500 text-center'>
                Quarantined images auto-delete after 72 hours
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageModeration;
